import { useMemo, useState } from "react";
import { useFeedbackList } from "@/hooks/queries/useFeedbackList";
import { useSignedStorageUrl } from "@/hooks/useSignedStorageUrl";
import type { FeedbackRow } from "@/hooks/queries/useFeedbackList";

type RatingFilter = "all" | "promoters" | "passives" | "detractors" | "unrated";

function StatCard({
  label,
  value,
  sub,
  accent,
}: {
  label: string;
  value: string;
  sub?: string;
  accent?: string;
}) {
  return (
    <div className="bg-white rounded-lg border border-gray-200 p-5">
      <p className="text-xs font-medium text-gray-500 uppercase tracking-wide mb-1">
        {label}
      </p>
      <p
        className="text-3xl font-bold"
        style={{ color: accent ?? "#111827" }}
      >
        {value}
      </p>
      {sub && <p className="text-xs text-gray-400 mt-1">{sub}</p>}
    </div>
  );
}

function ratingBucket(rating: number | null): RatingFilter {
  if (rating == null) return "unrated";
  if (rating >= 4) return "promoters";
  if (rating === 3) return "passives";
  return "detractors";
}

function ratingEmoji(rating: number | null): string {
  if (rating == null) return "💬";
  if (rating === 5) return "🤩";
  if (rating === 4) return "🙂";
  if (rating === 3) return "😐";
  if (rating === 2) return "😕";
  return "😠";
}

function FeedbackItem({ row }: { row: FeedbackRow }) {
  // Audio is click-to-load: audioPath stays null until the user clicks the button.
  const [audioPath, setAudioPath] = useState<string | null>(null);

  // Images are eagerly resolved so thumbnails appear without a click.
  const { data: imageUrl } = useSignedStorageUrl("feedback-images", row.image_path);
  const { data: audioUrl } = useSignedStorageUrl("feedback-audio", audioPath);

  return (
    <div className="bg-white rounded-lg border border-gray-200 p-4">
      <div className="flex items-start justify-between gap-4 mb-2">
        <div className="flex items-center gap-3">
          <span className="text-2xl">{ratingEmoji(row.rating)}</span>
          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs font-semibold text-gray-700">
            {row.rating != null ? `${row.rating} / 5` : "No rating"}
          </span>
          <span className="text-xs text-gray-400">
            {row.locale.toUpperCase()}
          </span>
          {row.app_version && (
            <span className="text-xs text-gray-400">
              v{row.app_version}
            </span>
          )}
        </div>
        <div className="text-right text-xs text-gray-400 whitespace-nowrap">
          {new Date(row.created_at).toLocaleString()}
        </div>
      </div>

      {row.transcript ? (
        <p className="text-sm text-gray-800 whitespace-pre-wrap mt-1">
          {row.transcript}
        </p>
      ) : (
        <p className="text-xs italic text-gray-400 mt-1">
          {row.audio_path
            ? "Audio only — transcription unavailable."
            : row.image_path
            ? "Screenshot only — no voice note."
            : row.rating != null
            ? "No voice note — rating only."
            : "No rating, no voice note."}
        </p>
      )}

      {row.audio_path && (
        <div className="mt-3">
          {audioUrl ? (
            <audio src={audioUrl} controls className="w-full h-8" />
          ) : (
            <button
              onClick={() => setAudioPath(row.audio_path)}
              className="rounded-md border border-gray-200 bg-white px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50"
            >
              ▶ Load audio
              {row.duration_seconds != null && ` · ${row.duration_seconds}s`}
            </button>
          )}
        </div>
      )}

      {row.image_path && (
        <div className="mt-3">
          {imageUrl ? (
            <a
              href={imageUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-block"
            >
              <img
                src={imageUrl}
                alt="Feedback screenshot"
                className="max-h-48 rounded-md border border-gray-200 hover:opacity-90 transition-opacity"
              />
            </a>
          ) : (
            <span className="text-xs italic text-gray-400">
              📸 Loading screenshot…
            </span>
          )}
        </div>
      )}

      <div className="mt-3 pt-2 border-t border-gray-100 flex items-center justify-between text-[11px] text-gray-400 font-mono">
        <span>user: {row.user_id.slice(0, 8)}…</span>
        <span>{row.id.slice(0, 8)}</span>
      </div>
    </div>
  );
}

export default function FeedbackDashboard() {
  const { rows, isLoading } = useFeedbackList();
  const [filter, setFilter] = useState<RatingFilter>("all");

  const stats = useMemo(() => {
    const now = Date.now();
    const thirtyDaysAgo = now - 30 * 24 * 60 * 60 * 1000;
    const recent = rows.filter(
      (r) => new Date(r.created_at).getTime() >= thirtyDaysAgo,
    );
    const total = recent.length;
    const promoters = recent.filter((r) => r.rating != null && r.rating >= 4).length;
    const passives = recent.filter((r) => r.rating === 3).length;
    const detractors = recent.filter((r) => r.rating != null && r.rating <= 2).length;
    const unrated = recent.filter((r) => r.rating == null).length;
    const rated = recent.filter((r) => r.rating != null);
    const avg = rated.length
      ? (rated.reduce((s, r) => s + (r.rating ?? 0), 0) / rated.length).toFixed(2)
      : "—";
    return { total, promoters, passives, detractors, unrated, avg };
  }, [rows]);

  const filtered = useMemo(() => {
    if (filter === "all") return rows;
    return rows.filter((r) => ratingBucket(r.rating) === filter);
  }, [rows, filter]);

  const pct = (n: number, total: number) =>
    total === 0 ? "0%" : `${Math.round((n / total) * 100)}%`;

  return (
    <div className="p-8">
      <div className="flex items-start justify-between mb-5">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Feedback</h1>
          <p className="text-sm text-gray-500 mt-0.5">
            NPS-style ratings with optional voice notes. Last 30 days shown in
            the summary; the list below shows up to 500 most-recent submissions.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-6 gap-3 mb-6">
        <StatCard
          label="Submissions (30d)"
          value={String(stats.total)}
          sub="rating, voice or screenshot"
        />
        <StatCard label="Avg rating" value={String(stats.avg)} sub="rated only" />
        <StatCard
          label="Promoters"
          value={pct(stats.promoters, stats.total)}
          sub={`${stats.promoters} · rating 4–5`}
          accent="#047857"
        />
        <StatCard
          label="Passives"
          value={pct(stats.passives, stats.total)}
          sub={`${stats.passives} · rating 3`}
          accent="#b45309"
        />
        <StatCard
          label="Detractors"
          value={pct(stats.detractors, stats.total)}
          sub={`${stats.detractors} · rating 1–2`}
          accent="#b91c1c"
        />
        <StatCard
          label="Unrated"
          value={pct(stats.unrated, stats.total)}
          sub={`${stats.unrated} · skipped NPS`}
          accent="#6b7280"
        />
      </div>

      <div className="flex items-center gap-2 mb-4">
        {(["all", "promoters", "passives", "detractors", "unrated"] as RatingFilter[]).map(
          (f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`rounded-md px-3 py-1.5 text-xs font-medium transition-colors ${
                filter === f
                  ? "bg-orange-50 text-orange-700 border border-orange-200"
                  : "bg-white text-gray-600 border border-gray-200 hover:bg-gray-50"
              }`}
            >
              {f === "all" ? "All" : f[0].toUpperCase() + f.slice(1)}
            </button>
          ),
        )}
      </div>

      {isLoading ? (
        <p className="text-sm text-gray-500">Loading...</p>
      ) : filtered.length === 0 ? (
        <p className="text-sm text-gray-500">No feedback yet.</p>
      ) : (
        <div className="space-y-3">
          {filtered.map((row) => (
            <FeedbackItem key={row.id} row={row} />
          ))}
        </div>
      )}
    </div>
  );
}
